import React, { useState, useEffect, memo } from 'react';
import styled, { keyframes } from 'styled-components';
// import Subnav from '../components/subNav';
import { useNavigate } from 'react-router-dom';
import { baseUrl } from '../config';
import SEO from '../components/SEO';
import createProjectBg from '../assets/img/createproject.jpg';

// ==================== THEME ====================
const TEAL = '#0fb3ad';
const TEAL_HOVER = '#0c9a95';
const TEAL_LIGHT = '#5eead4';
const TEXT = '#f3f5f7';
const MUTED = '#94a3b8';
const FIELD_BORDER = 'rgba(255, 255, 255, 0.14)';

// ==================== LEGACY BACKGROUND (kept, no longer rendered) ====================
// Simplified animation keyframes
const floatAnimation = keyframes`
  0% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.7;
  }
  100% {
    transform: translateY(-800px) rotate(360deg);
    opacity: 0;
  }
`;

const AnimatedBackground = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  overflow: hidden;
  background: linear-gradient(to bottom, #f9fafb, #f0fdfa);
`;

const AnimatedBox = styled.div`
  position: absolute;
  background: rgba(20, 184, 166, 0.15);
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  bottom: -150px;
  animation: ${floatAnimation} ${props => props.duration}s linear infinite;
  left: ${props => props.left}%;
  animation-delay: ${props => props.delay}s;
  will-change: transform, opacity;
  border-radius: ${props => props.round ? '50%' : '0'};

  &:nth-child(even) {
    background: rgba(15, 118, 110, 0.1);
  }
`;

const BackgroundPattern = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image:
    radial-gradient(circle at 25% 25%, rgba(20, 184, 166, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 75% 75%, rgba(20, 184, 166, 0.03) 0%, transparent 50%);
  z-index: -1;
`;

// ==================== PAGE BACKGROUND (new) ====================
const PageWrap = styled.div`
  position: relative;
  isolation: isolate;
  min-height: calc(100vh - 130px);
  border-radius: 0;
  overflow: hidden;
  background-color: #0b1420;
`;

const PageBg = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;
  background-image: url(${createProjectBg});
  background-size: cover;
  background-position: center;
  filter: brightness(1.05) saturate(1.1);
`;

const PageOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;
  background:
    radial-gradient(ellipse at center, rgba(8, 15, 25, 0.35) 0%, rgba(8, 15, 25, 0.72) 100%),
    linear-gradient(180deg, rgba(8, 15, 25, 0.25) 0%, rgba(8, 15, 25, 0.1) 40%, rgba(8, 15, 25, 0.45) 100%);
`;

// ==================== LAYOUT ====================
const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  position: relative;
  z-index: 1;

  @media (min-width: 768px) {
    padding: 1.5rem 2rem 2.5rem;
  }
`;

const ClockContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.25rem;
`;

const ClockWrapper = styled.div`
  background-color: rgba(13, 20, 32, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.625rem 0.75rem;
  border-radius: 12px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
  color: white;
`;

const DateDisplay = styled.div`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${TEAL_LIGHT};
  margin-bottom: 0.25rem;
`;

const DigitalDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.65);
  border-radius: 8px;
  padding: 0.375rem 0.5rem;
`;

// ==================== MAC OS CARD (dark glass) ====================
const MacOSCardContainer = styled.div`
  width: 100%;
  max-width: 50rem;
  margin: 0 auto;
  background-color: rgba(13, 20, 32, 0.8);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
`;

const WindowHeader = styled.div`
  position: relative;
  height: 2.25rem;
  background-color: rgba(255, 255, 255, 0.04);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  display: flex;
  align-items: center;
  padding: 0 1rem;
`;

const WindowTitle = styled.span`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: ${MUTED};
  pointer-events: none;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ControlButton = styled.div`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  border: 1px solid ${props => props.borderColor};
  background-color: ${props => props.bgColor};
`;

const CardContent = styled.div`
  padding: 1.5rem;

  @media (min-width: 768px) {
    padding: 1.75rem 2.25rem 2rem;
  }
`;

// ==================== FORM HEADER ====================
const FormHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.75rem;
`;

const HeaderIcon = styled.span`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: rgba(15, 179, 173, 0.14);
  border: 1px solid rgba(15, 179, 173, 0.4);
  color: ${TEAL_LIGHT};
`;

const Eyebrow = styled.p`
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${TEAL_LIGHT};
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${TEXT};
  margin: 0 0 0.375rem;

  @media (min-width: 768px) {
    font-size: 1.625rem;
  }
`;

const Subtitle = styled.p`
  color: ${MUTED};
  margin: 0;
  font-size: 0.9375rem;
`;

// ==================== FIELDS ====================
const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  color: ${TEXT};
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
`;

const Required = styled.span`
  color: #f87171;
  margin-left: 0.2rem;
`;

const HelpText = styled.p`
  color: ${MUTED};
  font-size: 0.8125rem;
  margin: 0 0 0.625rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.875rem;
  background-color: rgba(255, 255, 255, 0.04);
  border: 1px solid ${FIELD_BORDER};
  border-radius: 10px;
  color: ${TEXT};
  font-size: 0.9375rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;

  &::placeholder {
    color: #7c8a9e;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.24);
  }

  &:focus {
    outline: none;
    border-color: ${TEAL};
    background-color: rgba(255, 255, 255, 0.06);
    box-shadow: 0 0 0 3px rgba(15, 179, 173, 0.22);
  }
`;

const TextArea = styled(Input).attrs({ as: 'textarea' })`
  height: 8.5rem;
  resize: vertical;
  padding-left: 2.5rem;
  line-height: 1.5;
`;

const FieldIconWrap = styled.div`
  position: relative;
`;

const FieldIcon = styled.span`
  position: absolute;
  top: 0.8rem;
  left: 0.875rem;
  color: #7c8a9e;
  pointer-events: none;
  display: flex;
`;

// ==================== PROJECT TYPE ====================
const ProjectTypeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const ProjectTypeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.875rem;
  text-align: left;
  border-radius: 12px;
  border: 1px solid ${props => props.selected ? TEAL : FIELD_BORDER};
  background-color: ${props => props.selected ? 'rgba(15, 179, 173, 0.12)' : 'rgba(255, 255, 255, 0.03)'};
  color: ${TEXT};
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: ${props => props.selected ? TEAL : 'rgba(255, 255, 255, 0.28)'};
    background-color: ${props => props.selected ? 'rgba(15, 179, 173, 0.12)' : 'rgba(255, 255, 255, 0.06)'};
  }

  &:focus-visible {
    outline: 2px solid ${TEAL_LIGHT};
    outline-offset: 2px;
  }
`;

const TypeIcon = styled.span`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(15, 179, 173, 0.14);
  color: ${TEAL_LIGHT};
`;

const TypeText = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ProjectTypeName = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ProjectTypeDuration = styled.span`
  font-size: 0.75rem;
  margin-top: 0.125rem;
  color: ${MUTED};
`;

const Radio = styled.span`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 2px solid ${props => props.isChecked ? TEAL : 'rgba(255, 255, 255, 0.35)'};
  display: flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${TEAL};
    opacity: ${props => props.isChecked ? 1 : 0};
    transition: opacity 0.15s ease;
  }
`;

// ==================== SUBMIT ====================
const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  margin-top: 0.5rem;
  padding-top: 1.25rem;
`;

const SubmitButton = styled.button`
  padding: 0.75rem 1.5rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${props => props.disabled ? '#64748b' : '#fff'};
  border: none;
  border-radius: 10px;
  transition: background-color 0.2s ease;
  position: relative;
  overflow: hidden;
  background-color: ${props => props.disabled ? 'rgba(255, 255, 255, 0.08)' : TEAL};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  &:hover:not(:disabled) {
    background-color: ${TEAL_HOVER};
  }

  &:focus-visible {
    outline: 2px solid ${TEAL_LIGHT};
    outline-offset: 2px;
  }
`;

const ButtonText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: opacity 0.3s ease;
  opacity: ${props => props.invisible ? '0' : '1'};
`;

const ButtonIconContainer = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// ==================== ICONS (inline SVG) ====================
const Svg = ({ children, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const ClapperIcon = () => (
  <Svg size={24}>
    <path d="M4 11v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8H4z" />
    <path d="M4 11l-.88-3.12a1 1 0 0 1 .7-1.23l13.5-3.8a1 1 0 0 1 1.23.7L19.4 7" />
    <line x1="7.5" y1="5.9" x2="9.6" y2="9.7" />
    <line x1="12.3" y1="4.6" x2="14.4" y2="8.3" />
  </Svg>
);

const MegaphoneIcon = () => (
  <Svg size={18}>
    <path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 5.5a9 9 0 0 1 0 13" />
  </Svg>
);

const VideoIcon = () => (
  <Svg size={18}>
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" />
  </Svg>
);

const FilmReelIcon = () => (
  <Svg size={18}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="6.5" r="1.5" />
    <circle cx="12" cy="17.5" r="1.5" />
    <circle cx="6.5" cy="12" r="1.5" />
    <circle cx="17.5" cy="12" r="1.5" />
  </Svg>
);

const PencilIcon = () => (
  <Svg size={16}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
  </Svg>
);

const ArrowRightIcon = () => (
  <Svg size={18}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </Svg>
);

// MacOS Card Component
const MacOSCard = memo(({ children }) => {
  return (
    <MacOSCardContainer>
      <WindowHeader>
        <ControlButtons>
          <ControlButton bgColor="#ff5f56" borderColor="#e0443e" aria-label="close window" />
          <ControlButton bgColor="#ffbd2e" borderColor="#e1a116" aria-label="minimize window" />
          <ControlButton bgColor="#27c93f" borderColor="#1aab29" aria-label="maximize window" />
        </ControlButtons>
        <WindowTitle>New project</WindowTitle>
      </WindowHeader>
      <CardContent>
        {children}
      </CardContent>
    </MacOSCardContainer>
  );
});

// Legacy animated background — kept for reference, replaced by the photo background
// eslint-disable-next-line no-unused-vars
const BackgroundAnimation = () => {
  const boxes = Array.from({ length: 8 }).map((_, index) => {
    const size = Math.random() * 60 + 20;
    const left = Math.random() * 100;
    const duration = Math.random() * 15 + 15;
    const delay = Math.random() * 5;
    const round = Math.random() > 0.5;

    return (
      <AnimatedBox
        key={index}
        size={size}
        left={left}
        duration={duration}
        delay={delay}
        round={round}
      />
    );
  });

  return (
    <>
      <BackgroundPattern />
      <AnimatedBackground>{boxes}</AnimatedBackground>
    </>
  );
};

const PROJECT_TYPES = [
  { value: 'advertisement', name: 'Advertisement', duration: '15-60 seconds', Icon: MegaphoneIcon },
  { value: 'short', name: 'Short Film', duration: '5-30 minutes', Icon: VideoIcon },
  { value: 'full', name: 'Long Film', duration: '60+ minutes', Icon: FilmReelIcon },
];

const ProjectInitializer = () => {
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [projectTitle, setProjectTitle] = useState('');
  const [projectType, setProjectType] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [activeUser, setActiveUser] = useState();
  const navigate = useNavigate();
  const [genreInp, setGenreInp] = useState("");

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /* Set active user on page */

  async function getUserIdByToken() {
    try {
      const userId = await fetch(`${baseUrl}/getUserIDByToken`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (!userId.ok) {
        navigate("/login");
        return;
      }

      const id = await userId.json();
      setActiveUser(id.user_id);
      return id.user_id
    }
    catch { }
  }

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Set active user

    let userId = await getUserIdByToken();
    setActiveUser(userId);

    // API call
    try {
      // PLAN S5/S6 (G33/G40): the server-side auth for both calls below reads
      // this token, not the body, once PROJECT_CREATE_AUTH_MODE /
      // PROJECT_MEMBER_AUTH_MODE are set to enforce. In every mode today
      // (off/log) the body values above are still what's used, so this is a
      // no-op until the owner flips those flags.
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("access_token")}` };
      const createProjectRequest = await fetch(`${baseUrl}/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          title: projectTitle,
          project_desc: projectDescription,
          project_type: projectType,
          genre: genreInp,
          creator_id: userId
        }),
      });
      if (createProjectRequest.ok) {
        setIsLoading(false);
        const project = await createProjectRequest.json();
        // ✅ FIXED LINE 1
        const addMemberRequest = await fetch(`${baseUrl}/addProjectMember`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeader },
          body: JSON.stringify({
            user_id: userId,
            project_id: project.project_id  // ✅ FIXED
          })
        });

        // ✅ FIXED LINE 2
        if (addMemberRequest.ok) {
          setIsFormSubmitted(true);
          navigate("/toolselector/" + project.project_id);  // ✅ FIXED
        }
        else {
          setIsLoading(false);
        }
      }
      else {
        console.error("Project creation Failed")
      }
    }
    catch { }
  }

  // Digital clock digits for SVG rendering
  const timeString = formatTime(currentDateTime);
  const hours = timeString.substring(0, 2);
  const minutes = timeString.substring(3, 5);
  const seconds = timeString.substring(6, 8);

  return (
    <>
      <SEO
        title="Start a New Film Project"
        description="Start a new film project on ProducerBuddy and get instant access to AI-powered tools for script, budget, cast, and production management."
        url="https://producerbuddy.pilomation.com/project-starter"
        robots="noindex, nofollow"
      />
      {/* <Subnav /> */}
      <PageWrap>
        <PageBg />
        <PageOverlay />
        <Container>
          {/* Digital Clock Header */}
          <ClockContainer>
            <ClockWrapper>
              <DateDisplay>{formatDate(currentDateTime)}</DateDisplay>
              <DigitalDisplay>
                {/* SVG Digital Clock */}
                <svg width="180" height="40" viewBox="0 0 180 40" xmlns="http://www.w3.org/2000/svg">
                  {/* Hours */}
                  <g transform="translate(10,10)">
                    <rect x="0" y="0" width="28" height="30" rx="2" fill="#111" />
                    <text x="14" y="24" fontFamily="monospace" fontSize="24" fontWeight="bold" fill="#00e6cc" textAnchor="middle">{hours[0]}</text>
                  </g>
                  <g transform="translate(42,10)">
                    <rect x="0" y="0" width="28" height="30" rx="2" fill="#111" />
                    <text x="14" y="24" fontFamily="monospace" fontSize="24" fontWeight="bold" fill="#00e6cc" textAnchor="middle">{hours[1]}</text>
                  </g>

                  {/* Colon */}
                  <circle cx="80" cy="18" r="3" fill="#00e6cc" opacity={seconds % 2 === 0 ? "1" : "0.3"}>
                    <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="80" cy="18" r="3" fill="#00e6cc" opacity={seconds % 2 === 0 ? "1" : "0.3"}>
                    <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="80" cy="32" r="3" fill="#00e6cc" opacity={seconds % 2 === 0 ? "1" : "0.3"}>
                    <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite" />
                  </circle>

                  {/* Minutes */}
                  <g transform="translate(90,10)">
                    <rect x="0" y="0" width="28" height="30" rx="2" fill="#111" />
                    <text x="14" y="24" fontFamily="monospace" fontSize="24" fontWeight="bold" fill="#00e6cc" textAnchor="middle">{minutes[0]}</text>
                  </g>
                  <g transform="translate(122,10)">
                    <rect x="0" y="0" width="28" height="30" rx="2" fill="#111" />
                    <text x="14" y="24" fontFamily="monospace" fontSize="24" fontWeight="bold" fill="#00e6cc" textAnchor="middle">{minutes[1]}</text>
                  </g>

                  {/* Seconds */}
                  <g transform="translate(160,20)">
                    <text x="0" y="8" fontFamily="monospace" fontSize="16" fontWeight="bold" fill="#008575" textAnchor="start">{seconds}</text>
                  </g>
                </svg>
              </DigitalDisplay>
            </ClockWrapper>
          </ClockContainer>

          <MacOSCard>
            <FormHeader>
              <HeaderIcon><ClapperIcon /></HeaderIcon>
              <div>
                <Eyebrow>Create project</Eyebrow>
                <Title>Create your vision in reality with ProducerBuddy</Title>
                <Subtitle>Let's set up your new film project with all the essential details.</Subtitle>
              </div>
            </FormHeader>

            <form onSubmit={handleSubmit}>
              {/* Project Title */}
              <FormGroup>
                <Label htmlFor="projectTitle">Project Title<Required>*</Required></Label>
                <HelpText>Choose a memorable name that captures the essence of your project</HelpText>
                <Input
                  type="text"
                  id="projectTitle"
                  value={projectTitle}
                  onChange={(e) => setProjectTitle(e.target.value)}
                  placeholder="Enter your project title"
                  required
                />
              </FormGroup>

              {/* Project Type */}
              <FormGroup>
                <Label as="p">Project Type<Required>*</Required></Label>
                <HelpText>Select the format that best fits your creative vision</HelpText>
                <ProjectTypeContainer role="radiogroup" aria-label="Project type">
                  {PROJECT_TYPES.map(({ value, name, duration, Icon }) => (
                    <ProjectTypeButton
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={projectType === value}
                      selected={projectType === value}
                      onClick={() => setProjectType(value)}
                    >
                      <TypeIcon><Icon /></TypeIcon>
                      <TypeText>
                        <ProjectTypeName>{name}</ProjectTypeName>
                        <ProjectTypeDuration>{duration}</ProjectTypeDuration>
                      </TypeText>
                      <Radio isChecked={projectType === value} />
                    </ProjectTypeButton>
                  ))}
                </ProjectTypeContainer>
              </FormGroup>

              {/* Project Description */}
              <FormGroup>
                <Label htmlFor="projectDescription">Project Description<Required>*</Required></Label>
                <HelpText>Describe your thoughts, vision, and goals for this project</HelpText>
                <FieldIconWrap>
                  <FieldIcon><PencilIcon /></FieldIcon>
                  <TextArea
                    id="projectDescription"
                    value={projectDescription}
                    onChange={(e) => setProjectDescription(e.target.value)}
                    placeholder="Describe your thoughts about the project..."
                    required
                  />
                </FieldIconWrap>
              </FormGroup>

              {/* Genre */}
              <FormGroup>
                <Label htmlFor="projectGenre">Genre<Required>*</Required></Label>
                <HelpText>Enter the project's genre or genres (e.g. Drama, Thriller)</HelpText>
                <Input
                  type="text"
                  id="projectGenre"
                  value={genreInp}
                  onChange={(e) => setGenreInp(e.target.value)}
                  placeholder="Enter your project genre"
                  required
                />
              </FormGroup>

              {/* Submit Button */}
              <ButtonContainer>
                <SubmitButton
                  type="submit"
                  disabled={isLoading || !projectTitle || !projectType || !projectDescription || !genreInp}
                >
                  <ButtonText invisible={isLoading || isFormSubmitted}>
                    Let's begin the magic <ArrowRightIcon />
                  </ButtonText>

                  {isLoading && (
                    <ButtonIconContainer>
                      <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </ButtonIconContainer>
                  )}

                  {isFormSubmitted && (
                    <ButtonIconContainer>
                      <svg className="h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </ButtonIconContainer>
                  )}
                </SubmitButton>
              </ButtonContainer>
            </form>
          </MacOSCard>
        </Container>
      </PageWrap>
    </>
  );
};

export default ProjectInitializer;